import { useEffect, useCallback, useContext, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias, AI_MODULE_LIMIT } from "resources/account"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { useModules } from "../context/modules"
import { MODULE_TYPES } from "../modules/types"
import { ARTIFACT_KINDS } from "../modules/ai-artifact/types"
import type { Artifact, ArtifactKind, PositionSet } from "../modules/ai-artifact/types"
import { buildArtifactCacheKey } from "../modules/ai-artifact/gatherer"
import type { DashboardModule } from "../modules/types"
import { usePinWorkflow } from "connected-components/ai-teammate/hooks/use-pin-workflow"
import type { IDashboardContextValue } from "connected-components/dashboard/context/types"
import type { SelectedInstrument } from "helpers/types/instruments/selected"
import { log } from "helpers/logger"
import { getErrorMessage } from "helpers/dev-logger"
import { DashboardContext } from "../context/dashboard"

/**
 * Hook that listens for artifact pin requests from AI Teammate and handles them
 */

interface PinRequestPayload {
  artifact: Artifact
  kind: ArtifactKind
  title?: string
}

function toPositionSet(selection: SelectedInstrument | undefined): PositionSet | undefined {
  if (!selection || typeof selection.id !== "string" || typeof selection.type !== "string") {
    return undefined
  }

  return {
    id: selection.id,
    type: selection.type,
  }
}

type SuccessfullyPinnedArtifactModule = DashboardModule & {
  type: typeof MODULE_TYPES.AI_ARTIFACT
  config: DashboardModule["config"] & {
    artifactId: string
    pinnedId: string
    hasError?: false
  }
}

const isSuccessfullyPinnedArtifactModule = (module: DashboardModule): module is SuccessfullyPinnedArtifactModule =>
  module.type === MODULE_TYPES.AI_ARTIFACT
  && typeof module.config?.artifactId === "string"
  && typeof module.config?.pinnedId === "string"
  && module.config?.hasError !== true

const getPinnedArtifactIds = (modulesList: DashboardModule[]) =>
  modulesList
    .filter(isSuccessfullyPinnedArtifactModule)
    .map((module) => module.config.artifactId)

export function usePinArtifactHandler() {
  const { modules, isPreferencesLoaded, addModule, updateModule } = useModules()
  const aiModuleCount = useMemo(
    () => modules.filter((m) => m.type === MODULE_TYPES.AI_ARTIFACT).length,
    [modules],
  )
  const pinnedArtifactIds = useMemo(() => getPinnedArtifactIds(modules), [modules])
  const { mutateAsync: pinWorkflow } = usePinWorkflow()
  const accountAlias = useAccountAlias()
  const queryClient = useQueryClient()

  // DashboardContext originates from a JS createContext() call with no type parameter.
  // The cast is required for JS interop; all fields are accessed with optional chaining below.
  const { modelId, instrument, base, startDate, endDate } =
    (useContext(DashboardContext) as IDashboardContextValue | undefined) ?? {}

  const handlePinRequest = useCallback(async ({ artifact, kind, title }: PinRequestPayload) => {
    const existingModule = modules.find(
      (module) =>
        isSuccessfullyPinnedArtifactModule(module)
        && module.config?.artifactId === artifact.id,
    )
    if (existingModule) {
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PINNED, {
        artifactId: artifact.id,
        moduleId: existingModule.id,
        pinnedId: existingModule.config?.pinnedId ?? "",
        kind,
      })
      return
    }

    if (aiModuleCount >= AI_MODULE_LIMIT) {
      const errorMessage = `AI module limit of ${AI_MODULE_LIMIT} reached`
      log(`Cannot pin artifact: ${errorMessage}`)
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_FAILED, {
        artifactId: artifact.id,
        error: errorMessage,
        kind,
      })
      return
    }

    let moduleId: string | undefined

    try {
      // Add module with optimistic state
      moduleId = addModule({
        type: MODULE_TYPES.AI_ARTIFACT,
        config: {
          artifactId: artifact.id,
          kind,
          title: title ?? `AI ${kind === ARTIFACT_KINDS.PLOT ? "Plot" : "Table"}`,
          isPinning: true,
        },
      })

      // Store artifact in TanStack Query cache for optimistic rendering
      queryClient.setQueryData(["optimistic-artifact", moduleId], artifact)

      // Call API to pin workflow - backend generates and returns pinnedId
      const result = await pinWorkflow({
        accountAlias,
        artifactId: artifact.id,
        title: title ?? `AI ${kind === ARTIFACT_KINDS.PLOT ? "Plot" : "Table"}`,
      })

      // Seed React Query cache with the artifact using the correct cache key
      // This prevents refetch when RestDataIntegrator mounts
      const cacheKey = buildArtifactCacheKey(
        result.pinnedId,
        {
          modelId,
          positionSet: toPositionSet(instrument),
          base: toPositionSet(base),
          startDate,
          endDate,
        },
        accountAlias,
      )

      queryClient.setQueryData(cacheKey, { artifacts: [artifact] })

      // Store backend-generated pinnedId in config
      updateModule(moduleId, {
        config: {
          artifactId: artifact.id,
          kind,
          title: title ?? `AI ${kind === ARTIFACT_KINDS.PLOT ? "Plot" : "Table"}`,
          pinnedId: result.pinnedId,
          isPinning: false,
        },
      })

      // Remove optimistic cache now that real cache is seeded
      queryClient.removeQueries({ queryKey: ["optimistic-artifact", moduleId] })

      // Emit success event (for analytics, notifications, etc.)
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PINNED, {
        artifactId: artifact.id,
        moduleId,
        pinnedId: result.pinnedId,
        kind,
      })
    } catch (error) {
      const errorMessage = getErrorMessage(error)
      log(`Failed to pin artifact: ${errorMessage}`)

      if (moduleId) {
        // Keep cache for retry, just update error state
        updateModule(moduleId, {
          config: {
            isPinning: false,
            hasError: true,
          },
        })
      }

      // Emit failure event
      dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_FAILED, {
        artifactId: artifact.id,
        error: errorMessage,
        moduleId,
        kind,
      })
    }
  }, [modules, aiModuleCount, addModule, updateModule, pinWorkflow, accountAlias, queryClient, modelId, instrument, base, startDate, endDate])

  // Broadcast AI module limit status once preferences are loaded and whenever count, limit, or pinned artifact IDs change.
  useEffect(() => {
    if (!isPreferencesLoaded) return

    dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_LIMIT_STATUS, {
      count: aiModuleCount,
      isLimitReached: aiModuleCount >= AI_MODULE_LIMIT,
      pinnedArtifactIds,
    })
  }, [isPreferencesLoaded, aiModuleCount, pinnedArtifactIds])

  // Subscribe to pin requests
  useEffect(() => {
    return dashboardEvents.on<PinRequestPayload>(
      DASHBOARD_EVENTS.ARTIFACT_PIN_REQUESTED,
      handlePinRequest,
    )
  }, [handlePinRequest])
}
